import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import * as d3 from "d3";

const MARGIN = { top: 50, right: 30, bottom: 60, left: 120 }; 
const DENSITY_BAND_HEIGHT = 100;

type RidgelineProps = {
  data: { key: string; values: number[] }[];
  title: string;
  xLimMax: number;
  xLimMin: number;
};

const Ridgeline = ({ data, title, xLimMax, xLimMin }: RidgelineProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const X_LIMITS = [xLimMin, xLimMax];
  const [highlightedKey, setHighlightedKey] = useState<string | null>(null);
  const resizeTimeout = useRef<NodeJS.Timeout | null>(null);
  const handleMouseEnter = useCallback((key: string) => { //HOOK DE CALLBACK
        setHighlightedKey(key);
  }, []);

  const handleMouseLeave = useCallback(() => { //HOOK DE CALLBACK
      setHighlightedKey(null);
  }, []);


  useEffect(() => {
    const resizeObserver = new ResizeObserver(entries => {
      if (entries[0].contentRect) {
        if (resizeTimeout.current) {
          clearTimeout(resizeTimeout.current);
        }
        resizeTimeout.current = setTimeout(() => {
          setDimensions({
            width: entries[0].contentRect.width,
            height: entries[0].contentRect.height
          });
        }, 10);
      }
    });

    if (ref.current) {
      resizeObserver.observe(ref.current);
    }

    return () => {
      resizeObserver.disconnect();
      if (resizeTimeout.current) {
        clearTimeout(resizeTimeout.current);
      }
    };
  }, []);

  //CÁLCULO DE ÁREA DE DIBUJO
  const boundsWidth = dimensions.width - MARGIN.right - MARGIN.left;
  const boundsHeight = dimensions.height - MARGIN.top - MARGIN.bottom;

  const allGroups = useMemo(() => data.map(group => group.key), [data]);

  //Color de áreas
  const colorScale = useMemo(() => {
    // Escala que mapea el nombre del grupo (dominio) a un color (rango)
    return d3.scaleOrdinal()
        .domain(allGroups) // El dominio es la lista de nombres de categorías
        .range(d3.schemeCategory10); // El rango son los 10 colores estándar de D3
  }, [allGroups]);

  const xScale = useMemo(() => {
    if (boundsWidth <= 0) return d3.scaleLinear().domain(X_LIMITS).range([0, 0]);
    return d3.scaleLinear().domain(X_LIMITS).range([0, boundsWidth]).nice();
  }, [boundsWidth, X_LIMITS]);

  //CÁLCULO DE DENSIDADES (Bandwidth Dinámico)
  const densities = useMemo(() => {
    const allValues = data.flatMap(group => group.values);
    const standardDeviation = d3.deviation(allValues);
    
    // El bandwidth se ajusta a la dispersión de los datos
    const bandwidth = (standardDeviation || 0) * 0.1; 
    const finalBandwidth = Math.max(bandwidth, 10); 

    // Crea el estimador de densidad del kernel
    const kde = kernelDensityEstimator(kernelEpanechnikov(finalBandwidth), xScale.ticks(40));
    
    return data.map(group => ({
      key: group.key,
      density: kde(group.values) as [number, number][],
    }));
  }, [xScale, data]);

  //ESCALA Y DE DENSIDAD (Altura de la curva de 0 a 100)
  const densityMax = useMemo(() => 
    Math.max(...densities.map(group => Math.max(...group.density.map(y => y[1])))), 
    [densities]
  );

  const yScale = useMemo(() => 
    d3.scaleLinear().domain([0, densityMax]).range([DENSITY_BAND_HEIGHT, 0]), 
    [densityMax]
  );

  //ESCALA DE GRUPO (Posición Vertical de las categorías)
  const groupScale = useMemo(() => {
    if (boundsHeight <= 0) return d3.scaleBand().domain(allGroups).range([0, 0]).paddingInner(1);
    return d3.scaleBand().domain(allGroups).range([0, boundsHeight]).paddingInner(1);
  }, [allGroups, boundsHeight]);

  //DIBUJO DE EJES (D3 y useEffect)
  useEffect(() => {
    if (boundsWidth <= 0 || boundsHeight <= 0) return;
    
    // El grupo .axes ya está trasladado por MARGIN.left y MARGIN.top
    const axesGroup = d3.select(ref.current).select("svg").select(".axes");
    axesGroup.selectAll("*").remove(); 

    // 1. DIBUJAR EJE X (Abajo)
    const xAxisGenerator = d3.axisBottom(xScale);
    axesGroup
      .append("g")
      .attr("transform", `translate(0,${boundsHeight})`) // Posiciona en el fondo del área de dibujo
      .call(xAxisGenerator);
  }, [xScale, boundsHeight, groupScale]);

  //DIBUJO DE CURVAS (Paths JSX)
  const paths = useMemo(() => {
    if (densities.length === 0) return null;
    
    return densities.map((group, i) => {
      const isHighlighted = highlightedKey === group.key;
      //GENERADOR DE FORMA
      const pathGenerator = d3.line<[number, number]>()
        .x(d => xScale(d[0]))
        .y(d => yScale(d[1]) - DENSITY_BAND_HEIGHT) // Forma de la curva
        .curve(d3.curveBasis);

      //Opacidad Condicional: Si hay un destacado, usa 0.2 para los NO destacados
      let pathOpacity = 0.8;
      if (highlightedKey !== null && !isHighlighted) {
          pathOpacity = 0.2; // Atenuar los demás
      } else if (isHighlighted) {
          pathOpacity = 1.0; // Resaltar el activo
      }
        
      return (
        <path
          key={i}
          d={pathGenerator(group.density) as string}
          //POSICIONAMIENTO: Mueve la curva hacia su fila correcta (groupScale) 
          // y lo compensa hacia arriba con DENSITY_BAND_HEIGHT.
          transform={`translate(0, ${groupScale(group.key)!})`}
          fill={colorScale(group.key) as string}
          opacity={pathOpacity}
          stroke="black"
          strokeWidth={0.3}
          strokeLinejoin="round"
          onMouseEnter={() => handleMouseEnter(group.key)}
          onMouseLeave={handleMouseLeave}
        />
      );
    });
  }, [densities, xScale, yScale, groupScale]);

  //ETIQUETAS Y TÍTULO (JSX - Posición Absoluta Corregida)
  const labels = useMemo(() => 
    densities.map((group, i) => (
      <g 
        key={`label-group-${i}`}
        //POSICIONAMIENTO ABSOLUTO de las etiquetas (fuera del plot-area)
        transform={`translate(${MARGIN.left}, ${groupScale(group.key)! + MARGIN.top})`}
      >
        <circle
          cx={-MARGIN.left + 5} // Posiciona el círculo cerca del borde izquierdo
          cy={0} //Posición Y relativa al grupo (0)
          r={4} // Radio del círculo
          fill={colorScale(group.key) as string} // Usa el color de la curva
          stroke="none"
        />
        <text
          x={-MARGIN.left + 15} // Un poco a la derecha del círculo
          y={0} //Posición Y relativa al grupo (0)
          textAnchor="start"
          dominantBaseline="middle"
          fontSize={10}
          className="ridgeline-label"
          // Aplica el color del texto basado en el estado (HIGHLIGHT)
          fill={highlightedKey === group.key ? 'black' : '#888'} 
          fontWeight={highlightedKey === group.key ? 'bold' : 'normal'}
        >
          {abbreviateLabel(group.key, 20)}
          <title>{group.key}</title>
        </text>
      </g>
    )), 
    [densities, groupScale, highlightedKey, colorScale] // ¡Añade colorScale!
);

  return (
    <div ref={ref} style={{ width: '100%', height: '100%', minHeight: '300px' }}>
      <svg width={dimensions.width} height={dimensions.height}>
          <text
            x={dimensions.width / 2}
            y={MARGIN.top / 2}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={16}
            fontWeight="bold"
          >
            {wrapText(title, dimensions.width - MARGIN.left - MARGIN.right, 16)}
          </text>
        
        <g
          className="plot-area"
          width={boundsWidth}
          height={boundsHeight}
          transform={`translate(${MARGIN.left},${MARGIN.top})`} // Desplazamiento inicial
        >
          {paths}
        </g>
        
        <g className="labels-group">
            {labels}
        </g>
        
        <g
          className="axes"
          width={boundsWidth}
          height={boundsHeight}
          transform={`translate(${MARGIN.left},${MARGIN.top})`}
        />
       
      </svg>
    </div>
  );
};

export default Ridgeline;

function kernelDensityEstimator(kernel: any, X: any) {
  return function (V: any) {
    return X.map(function (x: any) {
      return [
        x,
        d3.mean(V, function (v: any) {
          return kernel(x - v);
        }),
      ];
    });
  };
}

function kernelEpanechnikov(k: any) {
  return function (v: any) {
    return Math.abs((v /= k)) <= 1 ? (0.75 * (1 - v * v)) / k : 0;
  };
}

function wrapText(text: string, maxWidth: number, fontSize: number): string {
  const words = text.split(' ');
  let line = '';
  const lines: string[] = [];
  for (let n = 0; n < words.length; n++) {
    const testLine = line + words[n] + ' ';
    const metrics = document.createElement('canvas').getContext('2d')!.measureText(testLine);
    const testWidth = metrics.width;
    if (testWidth > maxWidth && n > 0) {
      lines.push(line);
      line = words[n] + ' ';
    } else {
      line = testLine;
    }
  }
  lines.push(line);
  return lines.join('\n');
}

const abbreviateLabel = (label: string, maxLength = 15): string => {
  if (label.length <= maxLength) return label;
  let slice = label.slice(0,maxLength);
  slice = slice + '...' 
  return slice;
};
